import { ChangeEvent, FC, FormEvent, useRef, useState } from 'react';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import ImageSearchIcon from '@mui/icons-material/ImageSearch';
import { useTranslation } from 'react-i18next';
import { useBarcodeScanner } from '../hooks/useBarcodeScanner';
import { StepNav } from './StepNav';

const SCANNER_ELEMENT_ID = 'mpc-barcode-scanner';

type BarcodeScanStepProps = {
    active: boolean;
    hasProduct: boolean;
    onScanned: (barcode: string) => void;
    onNext: () => void;
};

export const BarcodeScanStep: FC<BarcodeScanStepProps> = ({ active, hasProduct, onScanned, onNext }) => {
    const { t } = useTranslation();
    const [manual, setManual] = useState('');
    const [toolMessage, setToolMessage] = useState('');
    const fileRef = useRef<HTMLInputElement>(null);
    const { status, errorMessage, isDecodingFile, scanFromFile, captureAndScan } = useBarcodeScanner({
        elementId: SCANNER_ELEMENT_ID,
        enabled: active,
        onScan: onScanned,
    });

    const handleManual = (e: FormEvent) => {
        e.preventDefault();
        const value = manual.trim();
        if (!value) return;
        onScanned(value);
    };

    const handleFile = async (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        setToolMessage('');
        const ok = await scanFromFile(file);
        if (!ok) {
            setToolMessage(t('mobileProductCapture.scan.decodeFailed'));
        }
    };

    const handleCapture = async () => {
        setToolMessage('');
        const result = await captureAndScan();
        if (result.ok) return;

        if (result.reason === 'decode_failed') {
            setToolMessage(t('mobileProductCapture.scan.decodeFailed'));
            return;
        }
        if (result.reason === 'not_ready') {
            setToolMessage(t('mobileProductCapture.scan.captureNotReady'));
            return;
        }
        setToolMessage(t('mobileProductCapture.scan.captureFailed'));
    };

    return (
        <div className="mpc-step mpc-step--scan">
            <header className="mpc-step__header">
                <h2>{t('mobileProductCapture.scan.title')}</h2>
                <p>{t('mobileProductCapture.scan.subtitle')}</p>
            </header>

            <div className="mpc-scanner-shell">
                <div id={SCANNER_ELEMENT_ID} className="mpc-scanner-viewport" />
                {(status === 'starting' || status === 'idle') && active && (
                    <div className="mpc-scanner-overlay">
                        <CircularProgress size={36} sx={{ color: 'var(--mpc-accent)' }} />
                        <span>{t('mobileProductCapture.scan.starting')}</span>
                    </div>
                )}
                {status === 'denied' && (
                    <div className="mpc-scanner-overlay mpc-scanner-overlay--error">
                        <span>{t('mobileProductCapture.scan.permissionDenied')}</span>
                    </div>
                )}
                {status === 'error' && (
                    <div className="mpc-scanner-overlay mpc-scanner-overlay--error">
                        <span>{t('mobileProductCapture.scan.error')}</span>
                        {errorMessage ? <small>{errorMessage}</small> : null}
                    </div>
                )}
                {isDecodingFile && (
                    <div className="mpc-scanner-overlay mpc-scanner-overlay--soft">
                        <CircularProgress size={28} sx={{ color: 'var(--mpc-accent)' }} />
                        <span>{t('mobileProductCapture.scan.decoding')}</span>
                    </div>
                )}
                {/* Decorative frame aligned to qrbox (~76% x 50%, centered) */}
                <div className="mpc-scanner-frame" aria-hidden />
            </div>

            <div className="mpc-scan-tools">
                <Button
                    type="button"
                    variant="outlined"
                    className="mpc-btn-ghost mpc-scan-tool-btn"
                    onClick={handleCapture}
                    disabled={status !== 'scanning' || isDecodingFile}
                    startIcon={<PhotoCameraIcon />}
                >
                    {t('mobileProductCapture.scan.captureFrame')}
                </Button>
                <Button
                    type="button"
                    variant="outlined"
                    className="mpc-btn-ghost mpc-scan-tool-btn"
                    onClick={() => fileRef.current?.click()}
                    disabled={isDecodingFile}
                    startIcon={<ImageSearchIcon />}
                >
                    {t('mobileProductCapture.scan.fromImage')}
                </Button>
                <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    className="mpc-hidden-input"
                    onChange={handleFile}
                />
            </div>

            {toolMessage ? <p className="mpc-hint mpc-hint--warn">{toolMessage}</p> : null}
            <p className="mpc-hint">{t('mobileProductCapture.scan.hint')}</p>

            <form className="mpc-manual-form" onSubmit={handleManual}>
                <TextField
                    fullWidth
                    size="small"
                    label={t('mobileProductCapture.scan.manualLabel')}
                    placeholder={t('mobileProductCapture.scan.manualPlaceholder')}
                    value={manual}
                    onChange={(e) => setManual(e.target.value)}
                    slotProps={{ htmlInput: { inputMode: 'numeric', autoComplete: 'off' } }}
                />
                <div className="mpc-manual-form__actions">
                    <Button type="submit" variant="contained" className="mpc-btn-primary" disabled={!manual.trim()}>
                        {t('mobileProductCapture.scan.manualSubmit')}
                    </Button>
                </div>
            </form>

            <StepNav
                hideBack
                onNext={onNext}
                nextDisabled={!hasProduct}
                nextLabel={t('mobileProductCapture.nav.next')}
            />
        </div>
    );
};
